import { CONTACT_ROLES, EMPTY_CONTACT } from '../utils/contacts';

// One contacts editor for every owner (issue #155). Replaces ClientContact,
// CorpContact and EmployeeContact, which were three components only because
// the backend had three contact models.
//
// `showRoles` is on for owners whose contacts drive invoice routing (clients).
// Everywhere else the role dropdown would be noise.

const cardStyle = {
  padding: '20px',
  background: 'var(--surface-soft)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-lg)',
  marginBottom: '12px',
  position: 'relative',
};

const hintStyle = {
  display: 'block',
  marginTop: '4px',
  fontFamily: 'var(--font-sans)',
  fontSize: '12px',
  color: 'var(--muted)',
  textTransform: 'none',
  letterSpacing: 0,
  fontWeight: 400,
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'var(--font-sans)',
  fontSize: '13px',
  color: 'var(--ink)',
  textTransform: 'none',
  letterSpacing: 0,
  fontWeight: 400,
  marginBottom: 0,
};

function ContactPanel({ contacts = [], setContacts, showRoles = false, addLabel = '+ Add Contact' }) {
  const add = () => setContacts([
    ...contacts,
    { ...EMPTY_CONTACT, primary: contacts.length === 0 },
  ]);

  const remove = (index) => {
    const next = contacts.filter((_, i) => i !== index);
    // the panel always keeps exactly one primary, so promote the first survivor
    if (contacts[index].primary && next.length > 0) {
      next[0] = { ...next[0], primary: true };
    }
    setContacts(next);
  };

  const change = (index, field, value) => {
    const next = [...contacts];
    next[index] = { ...next[index], [field]: value };
    setContacts(next);
  };

  const setPrimary = (index) => setContacts(
    contacts.map((c, i) => ({ ...c, primary: i === index })));

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <button type="button" onClick={add} className="btn-primary">{addLabel}</button>
      </div>

      {contacts.map((contact, index) => (
        <div key={contact.id || `new-${index}`} style={cardStyle}>
          <button
            type="button"
            onClick={() => remove(index)}
            className="btn-danger"
            style={{ position: 'absolute', top: '12px', right: '12px' }}
          >
            Remove
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={Boolean(contact.primary)}
                onChange={() => setPrimary(index)}
                style={{ width: 'auto', height: 'auto', margin: 0 }}
              />
              Primary contact
            </label>
          </div>

          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
            <label>
              Name
              <input
                type="text"
                value={contact.name || ''}
                onChange={(e) => change(index, 'name', e.target.value)}
              />
            </label>

            {showRoles ? (
              <label>
                Role
                <select
                  value={contact.role || ''}
                  onChange={(e) => change(index, 'role', e.target.value)}
                >
                  {CONTACT_ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                  {/* keep an unrecognised stored role selectable rather than silently resetting it */}
                  {contact.role && !CONTACT_ROLES.some(r => r.value === contact.role) && (
                    <option value={contact.role}>{contact.role}</option>
                  )}
                </select>
                <span style={hintStyle}>Invoice emails go to Billing contacts.</span>
              </label>
            ) : <span />}

            <label>
              Email
              <input
                type="email"
                value={contact.email1 || ''}
                onChange={(e) => change(index, 'email1', e.target.value)}
              />
            </label>

            <label>
              Phone
              <input
                type="tel"
                value={contact.phone1 || ''}
                onChange={(e) => change(index, 'phone1', e.target.value)}
              />
            </label>

            <label>
              Alternate email
              <input
                type="email"
                value={contact.email2 || ''}
                onChange={(e) => change(index, 'email2', e.target.value)}
              />
            </label>

            <label>
              Alternate phone
              <input
                type="tel"
                value={contact.phone2 || ''}
                onChange={(e) => change(index, 'phone2', e.target.value)}
              />
            </label>

            <label style={{ gridColumn: '1 / -1' }}>
              Address
              <input
                type="text"
                value={contact.addressLine1 || ''}
                onChange={(e) => change(index, 'addressLine1', e.target.value)}
                placeholder="Street address"
              />
            </label>

            <label>
              City
              <input
                type="text"
                value={contact.city || ''}
                onChange={(e) => change(index, 'city', e.target.value)}
              />
            </label>

            <label>
              State
              <input
                type="text"
                value={contact.state || ''}
                onChange={(e) => change(index, 'state', e.target.value)}
              />
            </label>

            <label>
              Zip
              <input
                type="text"
                value={contact.zip || ''}
                onChange={(e) => change(index, 'zip', e.target.value)}
              />
            </label>

            <label>
              Country
              <input
                type="text"
                value={contact.country || ''}
                onChange={(e) => change(index, 'country', e.target.value)}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ContactPanel;
